"use client";

import { useState, useMemo } from "react";
import type { CompetitionStats, GoalDistribution } from "fansunited-data-layer";
import { useCompetitionSlice } from "@/views/competition-shared/CompetitionSliceProvider";
import { FormTablesTab } from "../tabs/FormTablesTab";
import { LiveTableTab } from "../tabs/LiveTableTab";
import { StatsTab } from "../tabs/StatsTab";
import { BrandedHeader } from "@/components/branded/headers/BrandedHeader";
import type { ResolvedHeaderColors } from "@/features/theme/resolveBrandedStyle";
import { PillToggle } from "@/components/ui/controls/PillToggle";
import { TeamLogo } from "@/components/ui/TeamLogo";
import { AdSlot } from "@/features/ads/AdSlot";
import { PromoSlot } from "@/features/promo-slots/PromoSlot";
import type { ResolvedPromoRow } from "@/features/promo-slots/types";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import type { FUSportsStandingsWithNativeTeamIds, StageStandings } from "@/utilities/data/types";
import type { FormTables } from "@/utilities/data/competition/derive/types";
type Tab = "table" | "form" | "stats";
interface StandingsProps {
  /** Hide the branded header (used when CompetitionBrandedHeader is shown) */
  hideHeader?: boolean;
  /** Resolved header colors from section styles */
  headerColors?: ResolvedHeaderColors;
  /** All stages with standings (ACTIVE-first, INACTIVE last). Forwarded to LiveTableTab for stage tabs. */
  allStageStandings?: StageStandings[];
  /** Primary standings; undefined when the competition has none. */
  standings: FUSportsStandingsWithNativeTeamIds | undefined;
  formTables: FormTables;
  competitionStats: CompetitionStats;
  goalDistribution: GoalDistribution;
  /** Optional breadcrumbs element rendered inside the BrandedHeader (comp-site path). */
  breadcrumbs?: React.ReactNode;
  /** Resolved promos for the standings page. */
  promos?: (ResolvedPromoRow | null)[];
}
export function Standings({
  hideHeader = false,
  headerColors,
  allStageStandings,
  standings,
  formTables,
  competitionStats,
  goalDistribution,
  breadcrumbs,
  promos
}: StandingsProps) {
  const [activeTab, setActiveTab] = useState<Tab>("table");
  const {
    competition,
    seasonName
  } = useCompetitionSlice();
  const labels = useLabels().standings;
  const nav = useNavigation();
  const competitionHref = useMemo(() => {
    if (!competition?.id) return undefined;
    try {
      return nav.footballCompetition(competition.id);
    } catch {
      return undefined;
    }
  }, [competition?.id, nav]);
  const tabOptions = useMemo(() => [{
    id: "table" as const,
    label: labels.tabs.liveTable
  }, {
    id: "form" as const,
    label: labels.tabs.formTables
  }, {
    id: "stats" as const,
    label: labels.tabs.stats
  }], [labels]);
  const activeTabLabel = tabOptions.find(t => t.id === activeTab)?.label ?? "";
  if (!standings) {
    return <div className="min-h-screen">
                {!hideHeader && <BrandedHeader title={labels.title} subtitle={`${competition?.name || ""} ${seasonName || ""}`} imageUrl={competition?.assets?.logo} image={<TeamLogo src={competition?.assets?.logo} kind="competition" size="xl" />} href={competitionHref} colors={headerColors?.colors} backgroundImageUrl={headerColors?.backgroundImageUrl} breadcrumbs={breadcrumbs} />}
                <div className="container mx-auto px-4 py-8">
                    <div className="text-center py-12 text-muted-foreground">
                        <p className="text-lg font-semibold mb-2">{labels.noStandings}</p>
                        <p className="text-sm">{labels.noStandingsDescription}</p>
                    </div>
                </div>
            </div>;
  }
  return <>
            {!hideHeader && <BrandedHeader title={activeTabLabel || labels.title} subtitle={`${competition?.name ?? ""} ${seasonName ?? ""}`.trim()} imageUrl={competition?.assets?.logo ?? undefined} image={<TeamLogo src={competition?.assets?.logo} kind="competition" size="xl" />} href={competitionHref} colors={headerColors?.colors} backgroundImageUrl={headerColors?.backgroundImageUrl} breadcrumbs={breadcrumbs} />}

            <div className="max-w-350 w-full mx-auto px-4 py-8 space-y-8">
                <AdSlot id="content-top" />

                {/* Tab Navigation */}
                <PillToggle options={tabOptions} activeId={activeTab} onChange={setActiveTab} size="md" className="mb-6" />

                {/* Tab Content */}
                {activeTab === "table" && <LiveTableTab allStageStandings={allStageStandings} standings={standings} />}
                {activeTab === "form" && <FormTablesTab formTables={formTables} />}
                {activeTab === "stats" && <StatsTab competitionStats={competitionStats} goalDistribution={goalDistribution} />}

                <PromoSlot row={promos?.[0] ?? null} />

                <AdSlot id="content-bottom" />
            </div>
        </>;
}